import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageCircle, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import OnlineStatus from './OnlineStatus';

const ProjectChat = ({ projectId, currentUserId }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const chatInputRef = useRef(null);
  
  // @mention state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionCursorPosition, setMentionCursorPosition] = useState(0);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);

  // Get last read message ID from localStorage
  const getLastReadId = () => {
    return localStorage.getItem(`chat_lastread_${projectId}`) || '';
  };

  // Mark messages as read
  const markAsRead = () => {
    if (messages.length > 0) {
      localStorage.setItem(`chat_lastread_${projectId}`, messages[0].id);
      setUnreadCount(0);
    }
  };

  useEffect(() => {
    // Always fetch messages to count unread, even when collapsed
    fetchMessages();
    
    if (isExpanded) {
      fetchAllUsers();
      // Mark as read when expanded
      markAsRead();
    }
    
    // Poll for new messages every 10 seconds
    pollIntervalRef.current = setInterval(() => {
      fetchMessages();
    }, 10000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [projectId, isExpanded]);

  useEffect(() => {
    // Scroll to bottom when new messages arrive - only scroll within chat container
    if (isExpanded && messages.length > 0 && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      markAsRead();
    }
  }, [messages, isExpanded]);

  const fetchMessages = async () => {
    try {
      if (messages.length === 0) {
        setLoading(true);
      }
      const response = await axios.get(`${API}/projects/${projectId}/chat`);
      const newMessages = response.data;
      setMessages(newMessages);
      
      // Count unread messages (only if not expanded)
      if (!isExpanded) {
        const lastReadId = getLastReadId();
        if (lastReadId && newMessages.length > 0) {
          const lastReadIndex = newMessages.findIndex(m => m.id === lastReadId);
          if (lastReadIndex === -1) {
            // All messages are new
            setUnreadCount(newMessages.length);
          } else {
            setUnreadCount(lastReadIndex);
          }
        } else if (!lastReadId && newMessages.length > 0) {
          // Never read before - all are unread
          setUnreadCount(newMessages.length);
        }
      }
    } catch (error) {
      console.error('Failed to fetch chat messages', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setAllUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users', error);
    }
  };

  // @mention handlers
  const handleMessageChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setNewMessage(value);
    
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        setMentionFilter(textAfterAt.toLowerCase());
        setMentionCursorPosition(lastAtIndex);
        setShowMentionDropdown(true);
        setSelectedMentionIndex(0);
        return;
      }
    }
    setShowMentionDropdown(false);
  };

  const filteredMentionUsers = allUsers.filter(u => 
    u.name.toLowerCase().includes(mentionFilter) && u.id !== currentUserId
  );

  const handleSelectMention = (user) => {
    const beforeMention = newMessage.substring(0, mentionCursorPosition);
    const afterCursor = newMessage.substring(mentionCursorPosition + 1 + mentionFilter.length);
    const newValue = `${beforeMention}@${user.name} ${afterCursor}`;
    setNewMessage(newValue);
    setShowMentionDropdown(false);
    setMentionFilter('');
    setSelectedMentionIndex(0);
    
    const newCursorPosition = mentionCursorPosition + user.name.length + 2;
    setTimeout(() => {
      if (chatInputRef.current) {
        chatInputRef.current.focus();
        chatInputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 10);
  };

  const handleMessageKeyDown = (e) => {
    if (showMentionDropdown && filteredMentionUsers.length > 0) {
      if (e.key === 'Escape') {
        setShowMentionDropdown(false);
        e.preventDefault();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex(prev => 
          prev < filteredMentionUsers.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex(prev => 
          prev > 0 ? prev - 1 : filteredMentionUsers.length - 1
        );
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        handleSelectMention(filteredMentionUsers[selectedMentionIndex]);
      }
    }
  };

  // Format message with bold @mentions
  const formatMessageContent = (content) => {
    return content.split(/(@\w+(?:\s+\w+)?)/g).map((part, i) => 
      part.startsWith('@') ? (
        <span key={i} className="font-bold">{part}</span>
      ) : part
    );
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await axios.post(`${API}/projects/${projectId}/chat`, {
        content: newMessage.trim()
      });
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      toast.error('Fehler beim Senden der Nachricht');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Min.`;
    if (diffHours < 24) return `vor ${diffHours} Std.`;
    if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;
    return date.toLocaleDateString('de-CH', { 
      day: '2-digit', 
      month: '2-digit', 
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card data-testid="project-chat-card">
      <CardHeader 
        className="cursor-pointer hover:bg-muted/50 transition-colors py-3"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4" />
            Projekt-Chat
            {unreadCount > 0 && !isExpanded && (
              <span className="text-xs bg-destructive text-destructive-foreground px-1.5 py-0.5 rounded-full font-medium">
                {unreadCount} neu
              </span>
            )}
          </div>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          )}
        </CardTitle>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          {/* Messages List */}
          <div ref={messagesContainerRef} className="h-[280px] overflow-y-auto bg-muted/30 rounded-lg p-3 mb-3 space-y-3">
            {loading && messages.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                Laden...
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Noch keine Nachrichten</p>
                <p className="text-sm">Starten Sie die Konversation!</p>
              </div>
            ) : (
              messages.map((message) => {
                const isOwn = message.user_id === currentUserId;
                return (
                  <div
                    key={message.id}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        isOwn
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background border'
                      }`}
                    >
                      {!isOwn && (
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                            <span className="text-[10px] font-semibold text-primary">
                              {message.user_name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <span className="text-xs font-medium">{message.user_name}</span>
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap break-words">
                        {formatMessageContent(message.content)}
                      </p>
                      <div className={`text-[10px] mt-1 ${isOwn ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                        {formatTime(message.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input with @mention */}
          <form onSubmit={handleSendMessage} className="relative">
            {/* @mention dropdown */}
            {showMentionDropdown && filteredMentionUsers.length > 0 && (
              <div className="absolute bottom-full left-0 right-0 mb-1 bg-popover border border-border rounded-md shadow-lg z-50 max-h-40 overflow-y-auto">
                <div className="p-1">
                  <div className="text-xs text-muted-foreground px-2 py-1 border-b mb-1">
                    Benutzer erwähnen
                  </div>
                  {filteredMentionUsers.slice(0, 5).map((user, index) => (
                    <button
                      key={user.id}
                      type="button"
                      className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded text-left transition-colors ${
                        index === selectedMentionIndex
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-accent'
                      }`}
                      onClick={() => handleSelectMention(user)}
                      onMouseEnter={() => setSelectedMentionIndex(index)}
                    >
                      <div className="relative">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          index === selectedMentionIndex ? 'bg-primary-foreground/20' : 'bg-primary/10'
                        }`}>
                          <span className={`text-xs font-semibold ${
                            index === selectedMentionIndex ? 'text-primary-foreground' : 'text-primary'
                          }`}>
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <OnlineStatus 
                          isOnline={user.is_online} 
                          size="sm"
                          className="absolute -bottom-0.5 -right-0.5 border border-background"
                        />
                      </div>
                      <span className="flex-1 truncate">{user.name}</span>
                      {user.is_online && (
                        <span className={`text-[10px] px-1 py-0.5 rounded ${
                          index === selectedMentionIndex 
                            ? 'bg-green-400/30 text-green-100' 
                            : 'bg-green-500/10 text-green-600'
                        }`}>
                          online
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Input
                ref={chatInputRef}
                value={newMessage}
                onChange={handleMessageChange}
                onKeyDown={handleMessageKeyDown}
                placeholder="Nachricht schreiben... (@Name erwähnen)"
                className="flex-1"
                disabled={sending}
                data-testid="chat-input"
              />
              <Button 
                type="submit" 
                size="icon"
                disabled={!newMessage.trim() || sending}
                data-testid="send-chat-button"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </CardContent>
      )}
    </Card>
  );
};

export default ProjectChat;
